import api from './api';
import { getBrowserInfo } from '../utils/browserInfo';
import html2canvas from 'html2canvas';

/**
 * Error Logger Service
 * Captures and logs errors to the backend
 */
class ErrorLogger {
  constructor() {
    this.isEnabled = true;
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second
    this.captureScreenshots = true; // Enable/disable screenshot capture
    this.maxScreenshotSize = 500000; // 500KB max (base64 encoded)
  }

  /**
   * Log an error to the backend
   * @param {Object} errorData - Error information
   */
  async logError({
    errorType,
    severity = 'error',
    message,
    stackTrace = null,
    pageUrl = window.location.href,
    userAction = null,
    contextData = null
  }) {
    if (!this.isEnabled) {
      return;
    }

    try {
      const browserInfo = getBrowserInfo();

      // Capture screenshot if enabled and severity is error or critical
      let screenshotData = null;
      if (this.captureScreenshots && (severity === 'error' || severity === 'critical')) {
        screenshotData = await this.captureScreenshot();
      }

      const payload = {
        errorType,
        severity,
        message: this.truncateMessage(message),
        stackTrace: stackTrace ? this.truncateStackTrace(stackTrace) : null,
        pageUrl,
        userAction,
        browserInfo,
        contextData: this.sanitizeContext(contextData),
        screenshotData
      };

      // Send to backend with retry logic
      await this.sendWithRetry(payload);

    } catch (error) {
      // Don't let error logging break the app
      console.error('Failed to log error:', error);
    }
  }

  /**
   * Send error log with retry logic
   * @param {Object} payload - Error payload
   * @param {number} attempt - Current attempt number
   */
  async sendWithRetry(payload, attempt = 1) {
    try {
      await api.post('/error-logs', payload);
    } catch (error) {
      if (attempt < this.maxRetries) {
        // Wait and retry
        await new Promise(resolve => setTimeout(resolve, this.retryDelay * attempt));
        return this.sendWithRetry(payload, attempt + 1);
      }
      // Max retries reached, give up silently
      console.error('Error logging failed after', this.maxRetries, 'attempts');
    }
  }

  /**
   * Log a React component error
   * @param {Error} error - Error object
   * @param {Object} errorInfo - React error info
   * @param {string} userAction - What the user was doing
   */
  logReactError(error, errorInfo, userAction = null) {
    this.logError({
      errorType: 'react_error',
      severity: 'error',
      message: error.toString(),
      stackTrace: errorInfo?.componentStack || error.stack,
      userAction: userAction || 'Using React component',
      contextData: {
        componentStack: errorInfo?.componentStack
      }
    });
  }

  /**
   * Log an API error
   * @param {Object} error - Axios error object
   * @param {string} userAction - What the user was trying to do
   */
  logApiError(error, userAction = null) {
    const endpoint = error.config?.url || 'unknown';
    const method = error.config?.method?.toUpperCase() || 'unknown';
    const status = error.response?.status || 'network_error';

    this.logError({
      errorType: 'api_error',
      severity: status >= 500 ? 'error' : 'warning',
      message: `${method} ${endpoint} failed with status ${status}`,
      stackTrace: error.stack,
      userAction: userAction || `API request to ${endpoint}`,
      contextData: {
        endpoint,
        method,
        status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        requestData: this.sanitizeContext(error.config?.data)
      }
    });
  }

  /**
   * Log a validation error
   * @param {string} message - Error message
   * @param {Object} formData - Form data that failed validation
   * @param {string} userAction - What the user was trying to do
   */
  logValidationError(message, formData = null, userAction = null) {
    this.logError({
      errorType: 'validation_error',
      severity: 'warning',
      message,
      userAction: userAction || 'Submitting form',
      contextData: {
        formData: this.sanitizeContext(formData)
      }
    });
  }

  /**
   * Log a user action error (e.g., failed to save, failed to delete)
   * @param {string} action - Action that failed
   * @param {string} message - Error message
   * @param {Object} context - Additional context
   */
  logUserActionError(action, message, context = null) {
    this.logError({
      errorType: 'user_action_error',
      severity: 'error',
      message,
      userAction: action,
      contextData: context
    });
  }

  /**
   * Log a critical error (e.g., payment processing failure)
   * @param {string} message - Error message
   * @param {Object} context - Additional context
   */
  logCriticalError(message, context = null) {
    this.logError({
      errorType: 'critical_error',
      severity: 'critical',
      message,
      contextData: context
    });
  }

  /**
   * Truncate message to prevent huge payloads
   * @param {string} message - Error message
   * @returns {string} - Truncated message
   */
  truncateMessage(message) {
    const maxLength = 1000;
    if (message && message.length > maxLength) {
      return message.substring(0, maxLength) + '... (truncated)';
    }
    return message;
  }

  /**
   * Truncate stack trace to prevent huge payloads
   * @param {string} stackTrace - Stack trace
   * @returns {string} - Truncated stack trace
   */
  truncateStackTrace(stackTrace) {
    const maxLength = 5000;
    if (stackTrace && stackTrace.length > maxLength) {
      return stackTrace.substring(0, maxLength) + '\n... (truncated)';
    }
    return stackTrace;
  }

  /**
   * Sanitize context data to remove sensitive information
   * @param {Object} context - Context data
   * @returns {Object} - Sanitized context
   */
  sanitizeContext(context) {
    if (!context || typeof context !== 'object') {
      return context;
    }

    const sensitiveFields = [
      'password',
      'passwordConfirm',
      'currentPassword',
      'newPassword',
      'token',
      'accessToken',
      'refreshToken',
      'apiKey',
      'secret',
      'cardNumber',
      'cvv',
      'ssn',
      'passport'
    ];

    const sanitize = (obj) => {
      if (!obj || typeof obj !== 'object') return obj;

      const result = Array.isArray(obj) ? [] : {};

      for (const [key, value] of Object.entries(obj)) {
        const lowerKey = key.toLowerCase();
        const isSensitive = sensitiveFields.some(field => 
          lowerKey.includes(field.toLowerCase())
        );

        if (isSensitive) {
          result[key] = '[REDACTED]';
        } else if (typeof value === 'object' && value !== null) {
          result[key] = sanitize(value);
        } else {
          result[key] = value;
        }
      }

      return result;
    };

    return sanitize(context);
  }

  /**
   * Capture screenshot of current page
   * @returns {Promise<string|null>} Base64 encoded screenshot or null
   */
  async captureScreenshot() {
    try {
      // Capture the entire document body
      const canvas = await html2canvas(document.body, {
        allowTaint: true,
        useCORS: true,
        logging: false,
        scale: 0.5, // Reduce quality to save space
        width: window.innerWidth,
        height: window.innerHeight,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight
      });

      // Convert to base64
      let screenshot = canvas.toDataURL('image/jpeg', 0.6); // 60% quality

      // Check size and compress further if needed
      if (screenshot.length > this.maxScreenshotSize) {
        // Try lower quality
        screenshot = canvas.toDataURL('image/jpeg', 0.3);
        
        // If still too large, skip screenshot
        if (screenshot.length > this.maxScreenshotSize) {
          console.warn('Screenshot too large, skipping');
          return null;
        }
      }

      return screenshot;

    } catch (error) {
      console.error('Failed to capture screenshot:', error);
      return null;
    }
  }

  /**
   * Enable screenshot capture
   */
  enableScreenshots() {
    this.captureScreenshots = true;
  }

  /**
   * Disable screenshot capture
   */
  disableScreenshots() {
    this.captureScreenshots = false;
  }

  /**
   * Enable error logging
   */
  enable() {
    this.isEnabled = true;
  }

  /**
   * Disable error logging (e.g., for development)
   */
  disable() {
    this.isEnabled = false;
  }
}

// Create singleton instance
const errorLogger = new ErrorLogger();

export default errorLogger;
